import { InjectionToken } from '@angular/core';
import { MediaBreakpoints } from '@backbase/ui-ang/media-query-lib';

/**
 * Configuration interface for the layout service functionality.
 *
 * This interface defines the configuration options available for controlling
 * layout behavior, particularly around responsive breakpoints and collapse behavior.
 *
 * @example
 * ```typescript
 * import { LayoutConfig } from '@backbase/ui-ang/layout';
 *
 * const config: LayoutConfig = {
 *   collapseBreakpoint: 'md'
 * };
 * ```
 */
export interface LayoutConfig {
  /**
   * The media breakpoint at which layout elements should collapse.
   *
   * Defines the screen size threshold below which the layout will switch
   * to a collapsed/mobile view. Uses the standard media breakpoint values
   * from the design system.
   */
  collapseBreakpoint: MediaBreakpoints;
}

/**
 * Injection token for providing layout configuration to the LayoutService.
 *
 * This token allows the LayoutService to receive configuration through Angular's
 * dependency injection system. It should be provided at the module level with
 * a LayoutConfig object that defines layout behavior.
 *
 * @example
 * ```typescript
 * import { LAYOUT_CONFIG_TOKEN, LayoutConfig } from '@backbase/ui-ang/layout';
 *
 * // In your module providers
 * providers: [
 *   {
 *     provide: LAYOUT_CONFIG_TOKEN,
 *     useValue: {
 *       collapseBreakpoint: 'lg'
 *     } as LayoutConfig
 *   }
 * ]
 * ```
 */
export const LAYOUT_CONFIG_TOKEN = new InjectionToken<LayoutConfig>('injectable token for LayoutService');
